import {BehaviorSubject, from, fromEvent, merge, Observable, of, timer} from 'rxjs';
import {
  combineLatestWith,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {FirebaseDataService} from '../editor-state/firebase-data.service';
import {DiagramCanvasService} from '../diagram-render/diagram-canvas/diagram-canvas.service';
import {SpinnerService} from '../core/spinner/spinner.service';
import {ModeDataService} from '../editor-state/mode-data.service';
import * as _ from 'lodash';
import {ImageUploadService} from '../core/services/image.upload.service';
import {AngularComponentService} from '../diagram-render/mx-graph/graph/angular-component.service';
import {CircularDepCrutch} from '../core/helpers/circular-dep.crutch';
import {SidebarService} from '../editor-interface/sidebar/sidebar.service';
import {DtRecordsService} from '../diagram-interaction/services/dt-records.service';
import {MAPS_TYPES_ENUM} from '../editor-interface/shared/models/map.model';
import {MapService} from '../core/map/map.service';
import {AuthService} from '../core/auth/auth.service';
import {LocalStorageService} from 'src/app/core/services/local-storage.service';
import {CopyPasteConnectorData} from 'src/app/shared/models/copy-paste-connector-data.interface';
import {DE_CONNECTOR_LINK_CACHE} from 'src/app/shared/utils/constants';
import {Activity} from 'src/app/shared/models/activity.interface';
import {OrgModelNodesAttachment} from 'src/app/shared/models/org-model-nodes-attachment.interface';
import {FirebaseDatasnapshot} from '../shared/models/firebase-datasnapshot.interface';
import {DiagramTypeConfigService} from '../core/services/diagram-type-config.service';
import {DiagramMetaStoreService} from './diagram-meta-store.service';
import {PLAN_TYPES, Q9HtmlEncoderPipe} from '@q9elements/ui-kit/common';

declare let mxEvent: any;

export interface IActJson {
  mxKey?: number;
  number?: number;
  text?: string;
  strippedText?: string;
  position?: Position & Dimensions;
  image?: string;
  imageMode?: boolean;
  imageUrl?: string;
  customStyle?: boolean;
  container?: boolean;
  locked?: boolean | number;
  style?: any;
  resources?: IResourceJson[];
  attachments?: any[];
  refModelAttachments?: any[];
  requirementAttachments?: any[];
  storyAttachments?: any[];
  drilldowns?: any[];
  orgModelAttachments?: any[];
  footerText?: string;
  attributes?: IAttributeJson[];
  resourcesPerRow?: number;
  attributePadding?: boolean;
  orderIndex?: number;
  linkedDiagram?: string;
}

export interface IAttributeJson {
  mxKey: number;
  text: string;
  description?: string;
}

export interface IResourceJson {
  mxKey: number;
  activityMxKey: number;
  name: string;
  rasci?: {
    responsible: boolean;
    accountable: boolean;
    supporting: boolean;
    consulted: boolean;
    informed: boolean;
  };
  type: 'url' | 'text' | 'datatable' | 'image' | 'url_entry' | 'Human';
  resourceName?: string;
  resourceType?: string;
  resourceDbId?: string;
  id?: string;
  hidden?: boolean;
  main?: boolean;
  customStyle?: string;
  position?: number;
  icon?: string;
}

export interface IActImageJson extends IActJson {
  mxKey: number;
  image: string;
  imageUrl?: string;
}

export interface ITextJson {
  mxKey?: number;
  number?: number;
  text?: string;
  position?: Position & Dimensions & Rotation;
  customStyle?: boolean;
  locked?: boolean | number;
  link?: string;
  style?: any;
}

export interface IFlowlineJson {
  mxKey?: number;
  srcType?: Connector | Terminator | None;
  srcId?: keyof IActJson;
  tgtType?: Connector | Terminator | None;
  flForm?: {
    end: string;
    shape: string;
    start: string;
    type: string;
  };
  tgtId?: keyof IActJson;
  text?: string;
  position?: Position & {offset: Position} & {connectionPoints: ConnectionPoints};
  srcPosition?: Position;
  tgtPosition?: Position;
  waypoints?: Position[];
  connector?: any;
  terminator?: {flowlineMxKey: number};
  customStyle?: boolean;
  style?: any;
  lineTexts?: [ILineTextJson];
}

export interface ILineTextJson {
  key: string;
  text: string;
  position: Position & {offset: Position};
}

export interface IInteractionJson {
  flowlineMxKey: number;
  offset: Position;
  description?: string;
  style?: any;
  id?: string;
}

export interface IImageJson {
  mxKey?: number;
  position?: Position & Dimensions & RotationAlt;
  uuid?: string;
  copyImage?: boolean;
  locked?: boolean;
  link?: string;
  deleted?: boolean;
  url?: string;
  imageResized?: string;
}

export interface IStickerJson {
  mxKey?: number;
  text?: string;
  position?: Position & Dimensions & Rotation;
  paperClass?: string;
  locked?: boolean;
  author?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  editor?: any;
  updatedAt?: any;
  createdAt?: any;
  tags?: string[];
}

type Connector = 'connector';
type Terminator = 'terminator';
type None = 'none';

export interface ConnectionPoints {
  entryX: number;
  entryY: number;
  exitX: number;
  exitY: number;
}

export interface Position {
  x: number;
  y: number;
}

export interface Dimensions {
  h: number;
  w: number;
}

export interface Rotation {
  rotation: number | string; // string mxcellstyle node property
}

export interface RotationAlt {
  r: number | string;
}

@Injectable()
export class DiagramJsonHandlerService extends CircularDepCrutch {
  diagramJsonSubject: BehaviorSubject<any> = new BehaviorSubject({});
  diagramJson$: Observable<any> = this.diagramJsonSubject.asObservable();
  updateDTRecords$: Observable<boolean>;
  firstStart: any = true;
  setupObs: any;
  activityChanges: any;

  /** Injected in ImageUploadService constructor to avoid circular dependency **/
  public imageSvc: ImageUploadService;
  public isPro$: Observable<boolean> = this.diagramJson$.pipe(
    map(json => this.isPro(json)),
    shareReplay()
  );
  public isFree$: Observable<boolean> = this.diagramJson$.pipe(
    map(json => this.isFree(json)),
    shareReplay()
  );
  public isProTrial$: Observable<boolean> = this.diagramJson$.pipe(
    map(json => this.isProTrial(json)),
    shareReplay()
  );
  public isProTrialActive$: Observable<boolean> = this.diagramJson$.pipe(
    map(json => this.isProTrialActive(json)),
    shareReplay()
  );
  public isProActive$: Observable<boolean> = this.diagramJson$.pipe(
    map(json => this.isProActive(json)),
    shareReplay()
  );
  public isEnterpriseActive$: Observable<boolean> = this.diagramJson$.pipe(
    map(json => this.isEnterpriseActive(json)),
    shareReplay()
  );

  private firebaseRefsInitialized$ = this.fbDataSvc.refsInitialized$.pipe(
    filter(refsInitialized => refsInitialized),
    take(1)
  );

  constructor(
    private fbDataSvc: FirebaseDataService,
    private modeDataSvc: ModeDataService,
    private authService: AuthService,
    private diagramCanvasSvc: DiagramCanvasService,
    private spinnerSvc: SpinnerService,
    private angCompSvc: AngularComponentService,
    private sidebarSvc: SidebarService,
    private dtRecordsSvc: DtRecordsService,
    private mapService: MapService,
    private encodeHtml: Q9HtmlEncoderPipe,
    private diagramStoreService: DiagramMetaStoreService,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {
    super();
  }

  public get() {
    return this.diagramJsonSubject.getValue();
  }

  public setup() {
    this.firstStart = true;
    this.updateDTRecords$ = this.dtRecordsSvc.select();

    if (this.setupObs) {
      this.setupObs.unsubscribe();
    }

    const graph = this.diagramCanvasSvc.getGraph();
    const recentlyEdited$ = new Observable(observer => {
      const handler = () => observer.next();

      graph.addListener(mxEvent.CHANGE_COMMAND, handler);
      graph.addListener(mxEvent.CELLS_ADDED, handler);
      graph.addListener(mxEvent.CELLS_REMOVED, handler);

      return () => graph.removeListener(handler);
    }).pipe(
      switchMap(() => merge(of(true), timer(2500).pipe(map(() => false)))),
      startWith(false)
    );

    this.setupObs = this.firebaseRefsInitialized$
      .pipe(
        switchMap(() => fromEvent<unknown>(this.fbDataSvc.diagramContentRef, 'value')),
        combineLatestWith(recentlyEdited$),
        filter(([_, recentlyEdited]: any[]) => !recentlyEdited),
        map(([setup]: FirebaseDatasnapshot | undefined) =>
          Array.isArray(setup) ? setup[0] : setup
        ),
        withLatestFrom(this.modeDataSvc.currentMode$, this.updateDTRecords$), // => [snapshot, mode, dtrecords]
        tap(([_1, _2, addingRecord]) => {
          if (addingRecord) {
            this.dtRecordsSvc.set(false);
          }
        }),
        filter(
          ([_1, mode, addingRecord]) =>
            (!graph.isBusy() && !mode.edit && !addingRecord) || this.firstStart
        ),
        map(([dataSnapshot]) => (dataSnapshot ? dataSnapshot.val() : this.get())),
        switchMap(json => {
          this.firstStart = false;
          const oldJson = this.diagramJsonSubject.getValue() || {};

          if (oldJson.id !== json.id || oldJson.state !== json.state) {
            this.angCompSvc.clearCache();
          }

          if (json?.theme?.stylePath) {
            this.diagramStoreService.updateDiagram(json.diagramId, json.state, {
              stylePath: json.theme.stylePath
            });
          }

          // update map service with a relevant data before drawing,
          // because some rendering mechanisms differ depending on map type
          this.mapService.setup(json);

          if (
            json.diagramId !== oldJson.diagramId ||
            (this.isPro(json) &&
              json.theme &&
              oldJson.theme &&
              String(json.theme.id) !== String(oldJson.theme.id))
          ) {
            this.fbDataSvc.setStyleEvent(json.theme?.stylePath);

            return fromEvent(this.fbDataSvc.stylesRef, 'value').pipe(
              map(() => this.diagramCanvasSvc.drawFromJson(json)),
              map(() => json)
            );
          }

          return from(this.diagramCanvasSvc.drawFromJson(json)).pipe(map(() => json));
        })
      )
      .subscribe(json => {
        this.spinnerSvc.hide();
        this.diagramJsonSubject.next(json);
        this.imageSvc.enableImages(this.modeDataSvc.isEditMode());
        this.sidebarSvc.refresh();
      });
  }

  public setupActivityChanges() {
    if (this.activityChanges) {
      this.activityChanges.unsubscribe();
    }

    this.activityChanges = this.firebaseRefsInitialized$
      .pipe(
        switchMap(() => fromEvent(this.fbDataSvc.diagramActivitiesRef, 'child_changed')),
        withLatestFrom(this.modeDataSvc.currentMode$),
        map(([json, mode]) => {
          return {json: json[0], mode};
        }),
        filter(payload => {
          return payload.mode.edit;
        })
      )
      .subscribe((payload: any) => {
        const json = payload.json.val() || {};
        const activeCellMxKey = json.mxKey;

        // currently handle only attachments
        if (!json) {
          return;
        }

        if (json.attachments) {
          this.update({updateAttachmentList: json});
        }

        if (json.imageUrl) {
          this.update({updateActivityImage: json});
        }

        if (json.storyAttachments) {
          this.update({updateStoryAttachmentList: json});
        }

        this.diagramCanvasSvc.redrawActivity(activeCellMxKey);
      });
  }

  public get namings() {
    const {name: diagramName, levelNumber, map, topLevel} = this.get();

    return {
      diagramName,
      levelNumber,
      mapName: topLevel ? diagramName : map.name
    };
  }

  public isFree(optJson?) {
    const json = optJson || this.get();

    return _.get(json, 'team.plan.isNonBillable', false);
  }

  public isPro(optJson?) {
    const json = optJson || this.get();
    const teamPlan = (json.team || {}).plan || {};
    let isPro = false;

    if (
      (teamPlan.type === PLAN_TYPES.PRO && !teamPlan.trial) ||
      (teamPlan.type === PLAN_TYPES.PRO && teamPlan.trial && !teamPlan.trialExpired)
    ) {
      isPro = true;
    }

    return isPro;
  }

  public isProTrial(optJson?) {
    const json = optJson || this.get();
    const teamPlan = (json.team || {}).plan || {};

    return Boolean(teamPlan.trial);
  }

  public isProTrialActive(optJson?) {
    const json = optJson || this.get();
    const teamPlan = (json.team || {}).plan || {};

    return teamPlan.type === 'pro' && teamPlan.trial && !teamPlan.trialExpired;
  }

  public isProActive(optJson?) {
    const json = optJson || this.get();
    const teamPlan = (json.team || {}).plan || {};

    return (
      (teamPlan.type === 'pro' && !teamPlan.trial) || (teamPlan.trial && !teamPlan.trialExpired)
    );
  }

  public isEnterpriseActive(optJson?): boolean {
    const json = optJson || this.get();

    return _.get(json, 'team.plan.proPlus', false);
  }

  public getMapType(): MAPS_TYPES_ENUM {
    return _.get(this.get(), 'map.type', null);
  }

  public isCurrentTeam(teamId: string) {
    const json = this.get();

    return Boolean((json.team || {}).id === teamId);
  }

  public get groupTypeAll() {
    const groups = (this.get().team || {}).groups;

    if (Array.isArray(groups)) {
      return groups.find(group => group.systemType === 'all');
    }
  }

  public get state() {
    return this.diagramJsonSubject.getValue().state;
  }

  public update(data) {
    const method = Object.keys(data)[0];
    const json = this.diagramJsonSubject.getValue();

    if (!this[method]) {
      return;
    }

    const param = data[method];

    this[method](param, json);
    this.diagramJsonSubject.next({...json});
  }

  public checkSameTeam(teamId) {
    return this.get() && this.get().team.id === teamId;
  }

  private group(actions, json) {
    actions = actions || [];
    actions.forEach(action => {
      if (action) {
        const method = Object.keys(action)[0];

        if (!this[method]) {
          return;
        }

        const param = action[method];

        this[method](param, json);
      }
    });
  }

  private changeTitle(param, json) {
    json.name = param.value;
  }

  private changeDescription(param, json) {
    json.description = param.value;
  }

  private changeIsReadyToAuthorise(param, json) {
    json.readyToAuthorise = param.value;
  }

  private changeAuthorisationMode(param, json) {
    json.map.isAuthorisationEnabled = param.value;
  }

  /** activity **/

  private changeActivity(param: IActJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.activities) {
      json.activities = [];
    }

    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.mxKey);
    });

    if (activity) {
      activity.number = copyParam.number ? copyParam.number : activity.number;
      activity.text = copyParam.text || _.isString(copyParam.text) ? copyParam.text : activity.text;
      activity.position = copyParam.position ? copyParam.position : activity.position;
      activity.footerText =
        copyParam.footerText || _.isString(copyParam.footerText)
          ? copyParam.footerText
          : activity.footerText;

      if (copyParam.image !== void 0) {
        activity.image = copyParam.image;
      }

      if (copyParam.imageMode !== void 0) {
        activity.imageMode = copyParam.imageMode;
      }

      if (copyParam.imageUrl !== void 0) {
        activity.imageUrl = copyParam.imageUrl;
      }

      if (typeof copyParam.customStyle !== 'undefined') {
        activity.customStyle = copyParam.customStyle;
      }

      if (typeof copyParam.container !== 'undefined') {
        activity.container = copyParam.container;
      }

      if (typeof copyParam.locked !== 'undefined') {
        activity.locked = copyParam.locked;
      }

      if (copyParam.style || copyParam.customStyle) {
        activity.style = copyParam.style;
      }
    }
  }

  private reorderActivity(param, json) {
    const actIndex = _.findIndex(
      json.activities,
      (activity: any) => Number(activity.mxKey) === Number(param.mxKey)
    );

    if (typeof param.position !== 'undefined') {
      json.activities.splice(param.position, 0, json.activities.splice(actIndex, 1)[0]);
    }
  }

  private createActivity(param: IActJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.activities) {
      json.activities = [];
    }

    if (!copyParam.customStyle) {
      copyParam.customStyle = false;
    }

    const activity = {
      mxKey: copyParam.mxKey,
      number: copyParam.number,
      text: copyParam.text,
      footerText: copyParam.footerText,
      position: copyParam.position,
      image: copyParam.image,
      imageUrl: copyParam.imageUrl,
      imageMode: copyParam.imageMode,
      resources: copyParam.resources,
      attachments: copyParam.attachments,
      attributes: copyParam.attributes,
      refModelAttachments: copyParam.refModelAttachments,
      orgModelAttachments: copyParam.orgModelAttachments,
      requirementAttachments: copyParam.requirementAttachments,
      storyAttachments: copyParam.storyAttachments,
      customStyle: copyParam.customStyle,
      locked: copyParam.locked,
      container: copyParam.container,
      linkedDiagram: copyParam.linkedDiagram,
      attributePadding: copyParam.attributePadding
    };

    if (copyParam.style) {
      activity['style'] = copyParam.style;
    }

    if (typeof copyParam.orderIndex !== 'undefined') {
      json.activities.splice(copyParam.orderIndex, 0, activity);
    } else {
      json.activities.push(activity);
    }
  }

  private deleteActivity(param: IActJson, json) {
    _.remove(json.activities, (activity: any) => Number(activity.mxKey) === Number(param.mxKey));
  }

  private sendToChild(param, json) {
    if (!json.activities) {
      json.activities = [];
    }

    const newAct = param.parentActivity;

    const toRemove = param.actions.reduce(
      function (acc, curr) {
        if (curr) {
          if (curr.moveActivity) {
            acc.activities.push(+curr.moveActivity.mxKey);
          }

          if (curr.moveNote) {
            acc.notes.push(+curr.moveNote.mxKey);
          }

          if (curr.moveFlowline) {
            acc.flowlines.push(+curr.moveFlowline.mxKey);
          }

          if (curr.deleteInteraction) {
            acc.interactions.push(+curr.deleteInteraction.flowlineMxKey);
          }

          if (curr.moveInteraction) {
            acc.interactions.push(+curr.moveInteraction.flowlineMxKey);
          }

          if (curr.moveText) {
            acc.texts.push(+curr.moveText.mxKey);
          }

          if (curr.moveImage) {
            acc.images.push(+curr.moveImage.mxKey);
          }
        }

        return acc;
      },
      {
        activities: [],
        images: [],
        texts: [],
        flowlines: [],
        interactions: [],
        notes: []
      }
    );

    _.forOwn(toRemove, (val, key) => {
      if (json[key] && Array.isArray(json[key])) {
        _.remove(json[key], item => {
          const field = key === 'interactions' ? 'flowlineMxKey' : 'mxKey';

          return val.indexOf(item[field]) !== -1;
        });
      }
    });

    newAct.drilldowns = [];
    newAct.drilldowns.push({
      createdAt: new Date(),
      updatedAt: new Date(),
      targetDbId: param.diagramId,
      title: this.diagramTypeConfigService.activitySettings().text
    });
    json.activities.push(newAct);
  }

  private groupRenumber(param: {list: IActJson[]}, json) {
    const list = _.cloneDeep(param.list);

    list.forEach(item => {
      const activity = _.find(json.activities, act => {
        return +act.mxKey === +item.mxKey;
      });

      activity.number = item.number;
    });
  }

  private addAttribute(param, json) {
    const copyParam = _.cloneDeep(param);

    const attributeJson = {
      mxKey: copyParam.mxKey,
      text: copyParam.text
    };

    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.activityMxKey);
    });

    if (!activity.attributes) {
      activity.attributes = [];
    }

    if (typeof copyParam.position !== 'undefined') {
      const toIndex = copyParam.position;

      activity.attributes.splice(toIndex, 0, attributeJson);
    } else {
      activity.attributes.push(attributeJson);
    }
  }

  private deleteAttribute(param, json) {
    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(param.activityMxKey);
    });

    _.remove(activity.attributes, (att: any) => Number(att.mxKey) === Number(param.mxKey));
  }

  private changeAttribute(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.activityMxKey);
    });

    const attIndex = _.findIndex(
      activity.attributes,
      (r: any) => Number(r.mxKey) === Number(copyParam.mxKey)
    );
    const existingAttribute = activity.attributes.find(
      att => Number(att.mxKey) === Number(param.mxKey)
    );

    if (copyParam.text) {
      existingAttribute.text = copyParam.text;
    }

    if (copyParam.position) {
      const toIndex = copyParam.position - 1;
      const attributes = activity.attributes;

      attributes.splice(toIndex, 0, attributes.splice(attIndex, 1)[0]);
    }

    if (copyParam.description !== undefined) {
      existingAttribute.description = copyParam.description;
    }
  }

  changeResourcesPerRow(param, json) {
    const copyParam = _.cloneDeep(param);
    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.mxKey);
    });

    activity.resourcesPerRow = copyParam.resourcesPerRow;
  }

  changeAttributePadding(param, json) {
    const copyParam = _.cloneDeep(param);
    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.mxKey);
    });

    activity.attributePadding = copyParam.attributePadding;
  }

  /** drilldown **/

  private deleteDrilldown(param, json) {
    const activity = _.find(json.activities, act => act.mxKey === param.itemMxKey);

    if (activity) {
      delete activity.drilldowns;
    }
  }

  private linkDiagramAsDrilldown(param, json) {
    const activity = _.find(json.activities, act => act.mxKey === param.itemMxKey);

    if (activity) {
      activity.linkedDiagram = param.diagram;
    }
  }

  private removeLinkedDiagram(param, json) {
    const activity = _.find(json.activities, act => act.mxKey === param.itemMxKey);

    if (activity) {
      delete activity.linkedDiagram;
    }
  }

  private importDrilldown(param, json) {
    const activity = _.find(
      json.activities,
      act => +act.mxKey === parseInt(param.activityMxKey, 10)
    );

    if (!activity.drilldowns) {
      activity.drilldowns = [];
    }

    if (activity.drilldowns.length > 0) {
      return activity;
    }

    activity.drilldowns.push(param.drilldown);

    return activity;
  }

  private exportDrilldown(param, json) {
    const activity = _.find(
      json.activities,
      act => +act.mxKey === parseInt(param.activityMxKey, 10)
    );

    if (!activity.drilldowns) {
      activity.drilldowns = [];
    }

    if (activity.drilldowns.length > 0) {
      return activity;
    }

    activity.drilldowns.push(param.drilldown);

    return activity;
  }

  /** attachments & links **/
  private createAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const attachmentJson: any = {
      mxKey: copyParam.mxKey,
      title: copyParam.title,
      type: copyParam.type
    };

    if (copyParam.type === 'datatable') {
      attachmentJson.dataStructureDefinition = copyParam.dataStructureDefinition;
    }

    if (typeof copyParam.value !== 'undefined') {
      attachmentJson.value = copyParam.value;
    }

    if (copyParam.url) {
      attachmentJson.url = copyParam.url;
    }

    if (copyParam.type === 'url_entry') {
      attachmentJson.urlEntry = {
        title: copyParam.title,
        value: this.encodeHtml.transform(copyParam.value, true),
        tags: copyParam.tags,
        description: copyParam.description
      };
      attachmentJson.value = this.encodeHtml.transform(copyParam.value, true);
    }

    if (copyParam.type === 'text') {
      attachmentJson.savedOnS3 = copyParam.savedOnS3;
      attachmentJson.noteProItems = copyParam.noteProItems;
    }

    if (copyParam.groups) {
      attachmentJson.groups = copyParam.groups;
    }

    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.itemMxKey);
    });

    if (!activity.attachments) {
      activity.attachments = [];
    }

    activity.attachments.push(attachmentJson);
  }

  private deleteAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.itemMxKey);
    });

    if (!activity.attachments) {
      activity.attachments = [];
    }

    _.remove(activity.attachments, function (attachmentJson: any) {
      return Number(attachmentJson.mxKey) === Number(copyParam.mxKey);
    });
  }

  private createUrlRecordsAttachments(param, json) {
    const copyParam = _.cloneDeep(param);

    const attachmentsArray = copyParam.map(item => {
      return attachmentInterface(item);
    });

    function attachmentInterface(item) {
      const obj = {
        mxKey: item.mxKey,
        title: item.title,
        type: item.type,
        value: item.value,
        groups: item.groups,
        urlEntry: urlEntryInterface(item)
      };

      return _.pickBy(obj, _.identity);
    }

    function urlEntryInterface(item) {
      let obj = {};

      if (typeof item.urlEntry === 'object') {
        obj = {
          id: item.urlEntry.id,
          title: item.urlEntry.title,
          value: item.urlEntry.value,
          tags: item.urlEntry.tags,
          description: item.urlEntry.description,
          state: item.urlEntry.state
        };
      } else {
        obj = {
          id: item.urlEntry,
          title: item.title,
          value: item.value,
          tags: item.tags,
          description: item.description,
          state: item.state
        };
      }

      return _.pickBy(obj, _.identity);
    }

    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam[0].itemMxKey);
    });

    if (!activity.attachments) {
      activity.attachments = [];
    }

    for (let i = 0, length = attachmentsArray.length; i < length; i++) {
      activity.attachments.push(attachmentsArray[i]);
    }
  }

  private deleteUrlRecordsAttachments(param, json) {
    const copyParam = _.cloneDeep(param);
    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.itemMxKey);
    });

    if (!activity.attachments) {
      activity.attachments = [];
    }

    _.remove(activity.attachments, function (attachmentJson: any) {
      return _.some(copyParam.mxKeys, function (mxKey) {
        return Number(attachmentJson.mxKey) === Number(mxKey);
      });
    });
  }

  private createRefModelAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => {
      return +act.mxKey === +copyParam.itemMxKey;
    });

    if (!activity.refModelAttachments) {
      activity.refModelAttachments = [];
    }

    activity.refModelAttachments.push(copyParam);
  }

  private deleteRefModelAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => +act.mxKey === +copyParam.itemMxKey);

    if (!activity.refModelAttachments) {
      activity.refModelAttachments = [];
    }

    _.remove(
      activity.refModelAttachments,
      (attachmentJson: any) => parseInt(attachmentJson.mxKey, 10) === parseInt(copyParam.mxKey, 10)
    );
  }

  private changeRefModelAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => +act.mxKey === +copyParam.itemMxKey);

    if (!activity.refModelAttachments) {
      activity.refModelAttachments = [];
    }

    const index = _.findIndex(
      activity.refModelAttachments,
      (chr: any) => parseInt(chr.mxKey, 10) === parseInt(copyParam.mxKey, 10)
    );

    if (copyParam.title) {
      activity.refModelAttachments[index].title = copyParam.title;
    }

    if (copyParam.description || copyParam.description === '') {
      activity.refModelAttachments[index].description = copyParam.description;
    }

    if (copyParam.groups) {
      activity.refModelAttachments[index].groups = copyParam.groups;
    }

    if (typeof copyParam.position !== 'undefined') {
      const toIndex = copyParam.position;

      activity.refModelAttachments.splice(
        toIndex,
        0,
        activity.refModelAttachments.splice(index, 1)[0]
      );
    }
  }

  private createOrgModelAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => {
      return +act.mxKey === +copyParam.itemMxKey;
    });

    if (!activity.orgModelAttachments) {
      activity.orgModelAttachments = [];
    }

    activity.orgModelAttachments.push(copyParam);
  }

  private deleteOrgModelAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => +act.mxKey === +copyParam.itemMxKey);

    if (!activity.orgModelAttachments) {
      activity.orgModelAttachments = [];
    }

    _.remove(
      activity.orgModelAttachments,
      (attachmentJson: any) => parseInt(attachmentJson.mxKey, 10) === parseInt(copyParam.mxKey, 10)
    );
  }

  private changeOrgModelAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => +act.mxKey === +copyParam.itemMxKey);

    if (!activity.orgModelAttachments) {
      activity.orgModelAttachments = [];
    }

    const index = _.findIndex(
      activity.orgModelAttachments,
      (chr: any) => parseInt(chr.mxKey, 10) === parseInt(copyParam.mxKey, 10)
    );

    if (copyParam.title) {
      activity.orgModelAttachments[index].title = copyParam.title;
    }

    if (copyParam.description || copyParam.description === '') {
      activity.orgModelAttachments[index].description = copyParam.description;
    }

    if (copyParam.groups) {
      activity.orgModelAttachments[index].groups = copyParam.groups;
    }

    if (typeof copyParam.position !== 'undefined') {
      const toIndex = copyParam.position;

      activity.orgModelAttachments.splice(
        toIndex,
        0,
        activity.orgModelAttachments.splice(index, 1)[0]
      );
    }
  }

  private createOrgModelNodesAttachment(
    orgModelNodesAttachments: OrgModelNodesAttachment[],
    json?
  ): void {
    const [attachment] = orgModelNodesAttachments;
    const activity = json?.activities?.find(
      (activity: Activity) => activity.mxKey == attachment.itemMxKey
    );

    if (!activity) {
      return;
    }

    if (!activity.orgModelAttachments) {
      activity.orgModelAttachments = [];
    }

    const attachedNodesIds = activity.orgModelAttachments.map(({sfNode}) => sfNode._id);
    const attachmentsToAdd = orgModelNodesAttachments.filter(
      ({sfNode}) => !attachedNodesIds.includes(sfNode._id)
    );

    activity.orgModelAttachments.push(...attachmentsToAdd);
  }

  private deleteOrgModelNodesAttachment(
    orgModelNodesAttachment: OrgModelNodesAttachment[],
    json?
  ): void {
    const [attachment] = orgModelNodesAttachment;
    const activity = json?.activities?.find(
      (activity: Activity) => activity.mxKey == attachment.itemMxKey
    );

    if (!activity) {
      return;
    }

    if (!activity.orgModelAttachments) {
      activity.orgModelAttachments = [];
    }

    const attachmentsToRemoveMxKeys = orgModelNodesAttachment.map(({mxKey}) => mxKey);
    const filteredActivityOrgModelAttachments = activity.orgModelAttachments.filter(
      ({mxKey}) => !attachmentsToRemoveMxKeys.includes(mxKey)
    );

    activity.orgModelAttachments = filteredActivityOrgModelAttachments;
  }

  private changeAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, function (act) {
      return Number(act.mxKey) === Number(copyParam.itemMxKey);
    });

    if (!activity.attachments) {
      activity.attachments = [];
    }

    const index = _.findIndex(activity.attachments, function (chr: any) {
      return Number(chr.mxKey) === Number(copyParam.mxKey);
    });

    if (copyParam.title) {
      activity.attachments[index].title = copyParam.title;
    }

    if (copyParam.value || copyParam.value === '') {
      activity.attachments[index].value = copyParam.value;
    }

    if (copyParam.description || copyParam.description === '') {
      activity.attachments[index].description = copyParam.description;
    }

    if (copyParam.groups) {
      activity.attachments[index].groups = copyParam.groups;
    }

    if (copyParam.hasOwnProperty('savedOnS3')) {
      activity.attachments[index].savedOnS3 = copyParam.savedOnS3;
    }

    if (copyParam.hasOwnProperty('dtRecordsCount')) {
      activity.attachments[index].dtRecordsCount = copyParam.dtRecordsCount;
    }

    if (copyParam.hasOwnProperty('viewersCanAddRecords')) {
      activity.attachments[index].viewersCanAddRecords = copyParam.viewersCanAddRecords;
    }

    if (copyParam.hasOwnProperty('noteProItems')) {
      activity.attachments[index].noteProItems = copyParam.noteProItems;
    }

    if (typeof copyParam.position !== 'undefined') {
      const toIndex = copyParam.position;

      activity.attachments.splice(toIndex, 0, activity.attachments.splice(index, 1)[0]);
    }
  }

  private updateAttachmentList(param, json) {
    const copyParam = _.cloneDeep(param);
    const activity = _.find(json.activities, act => Number(act.mxKey) === Number(copyParam.mxKey));

    if (!activity) {
      return;
    }

    param.attachments.forEach((i, index) => {
      // handled only image attachments
      if (
        i.hasOwnProperty('url') &&
        activity.attachments[index] &&
        !activity.attachments[index].hasOwnProperty('url')
      ) {
        activity.attachments[index].url = i.url;
      }

      if (
        i.type === 'url_entry' &&
        activity.attachments[index] &&
        !activity.attachments[index].urlEntry?.id
      ) {
        activity.attachments[index].urlEntry = {...i.urlEntry};
      }
    });
  }

  private updateStoryAttachmentList(param, json) {
    const copyParam = _.cloneDeep(param);
    const activity = _.find(json.activities, act => Number(act.mxKey) === Number(copyParam.mxKey));

    activity.storyAttachments = param.storyAttachments;
  }

  private createRequirementAttachment(param, json) {
    let copyParam = _.cloneDeep(param);

    if (!Array.isArray(copyParam)) {
      copyParam = [copyParam];
    }

    _.forEach(copyParam, el => {
      const attachmentJson = _.pick(el, [
        'mxKey',
        'title',
        'description',
        'requirement',
        'itemMxKey',
        'reqId',
        'reqReason',
        'reqDetails',
        'type',
        'groups',
        'status'
      ]);

      const activity = _.find(json.activities, act => +act.mxKey === +el.itemMxKey);

      if (!activity.requirementAttachments) {
        activity.requirementAttachments = [];
      }

      if (el.groups) {
        attachmentJson.groups = el.groups;
      }

      activity.requirementAttachments.push(attachmentJson);
    });
  }

  private createStoryAttachment(param, json) {
    let copyParam = _.cloneDeep(param);

    if (!Array.isArray(copyParam)) {
      copyParam = [copyParam];
    }

    _.forEach(copyParam, el => {
      const attachmentJson = _.pick(el, [
        'mxKey',
        'title',
        'description',
        'story',
        'itemMxKey',
        'storyId',
        'status',
        'issueUrl',
        'type',
        'groups'
      ]);

      const activity = _.find(json.activities, act => +act.mxKey === +el.itemMxKey);

      if (!activity.storyAttachments) {
        activity.storyAttachments = [];
      }

      if (el.groups) {
        attachmentJson.groups = el.groups;
      }

      activity.storyAttachments.push(attachmentJson);
    });
  }

  private changeRequirementAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => +act.mxKey === +copyParam.itemMxKey);

    if (!activity.requirementAttachments) {
      activity.requirementAttachments = [];
    }

    const index = _.findIndex(
      activity.requirementAttachments,
      (chr: any) => parseInt(chr.mxKey, 10) === parseInt(copyParam.mxKey, 10)
    );

    if (copyParam.title) {
      activity.requirementAttachments[index].title = copyParam.title;
    }

    if (copyParam.description || copyParam.description === '') {
      activity.requirementAttachments[index].description = copyParam.description;
    }

    if (copyParam.groups) {
      activity.requirementAttachments[index].groups = copyParam.groups;
    }

    if (typeof copyParam.position !== 'undefined') {
      const toIndex = copyParam.position;

      activity.requirementAttachments.splice(
        toIndex,
        0,
        activity.requirementAttachments.splice(index, 1)[0]
      );
    }
  }

  private changeStoryAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => +act.mxKey === +copyParam.itemMxKey);

    if (!activity.storyAttachments) {
      activity.storyAttachments = [];
    }

    const index = _.findIndex(
      activity.storyAttachments,
      (chr: any) => parseInt(chr.mxKey, 10) === parseInt(copyParam.mxKey, 10)
    );

    if (copyParam.title) {
      activity.storyAttachments[index].title = copyParam.title;
    }

    if (copyParam.description || copyParam.description === '') {
      activity.storyAttachments[index].description = copyParam.description;
    }

    if (copyParam.groups) {
      activity.storyAttachments[index].groups = copyParam.groups;
    }

    if (typeof copyParam.position !== 'undefined') {
      const toIndex = copyParam.position;

      activity.storyAttachments.splice(toIndex, 0, activity.storyAttachments.splice(index, 1)[0]);
    }
  }

  private deleteRequirementAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => +act.mxKey === +copyParam.itemMxKey);

    if (!Array.isArray(copyParam.mxKey)) {
      copyParam.mxKey = [copyParam.mxKey];
    }

    if (!activity.requirementAttachments) {
      activity.requirementAttachments = [];
    }

    _.remove(
      activity.requirementAttachments,
      (attachmentJson: any) => copyParam.mxKey.indexOf(attachmentJson.mxKey) >= 0
    );
  }

  private deleteStoryAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, act => +act.mxKey === +copyParam.itemMxKey);

    if (!Array.isArray(copyParam.mxKey)) {
      copyParam.mxKey = [copyParam.mxKey];
    }

    if (!activity.storyAttachments) {
      activity.storyAttachments = [];
    }

    _.remove(
      activity.storyAttachments,
      (attachmentJson: any) => copyParam.mxKey.indexOf(attachmentJson.mxKey) >= 0
    );
  }

  private convertTolUrlEntryAttachment(param, json) {
    const copyParam = _.cloneDeep(param);

    const activity = _.find(json.activities, function (act) {
      return +act.mxKey === +copyParam.itemMxKey;
    });

    if (!activity.attachments) {
      activity.attachments = [];
    }

    const currentIndex = _.findIndex(
      activity.attachments,
      (chr: any) => parseInt(chr.mxKey, 10) === parseInt(copyParam.mxKey, 10)
    );

    const newAttachment = generateNewAttachment(copyParam);

    function generateNewAttachment(item) {
      const attachment: any = {
        title: item.title,
        value: item.value,
        type: item.type,
        groups: item.groups,
        activityMxKey: item.activityMxKey,
        mxKey: item.mxKey,
        urlEntry: undefined
      };

      if (item.type === 'url_entry') {
        attachment.urlEntry = urlEntryInterface(item);
      }

      return _.pickBy(attachment, _.identity);
    }

    function urlEntryInterface(item) {
      const isObject = typeof item.urlEntry === 'object';
      const prefix = isObject ? item.urlEntry : item;

      const entryObject = {
        id: isObject ? prefix.id : prefix.urlEntry,
        title: prefix.title,
        value: prefix.value,
        tags: prefix.tags,
        description: prefix.description
      };

      return _.pickBy(entryObject, _.identity);
    }

    activity.attachments.splice(currentIndex, 1, newAttachment);
  }

  private addLineText(param, json) {
    const copyParam = _.cloneDeep(param);
    const flowline = _.find(
      json.flowlines || [],
      fl => Number(fl.mxKey) === Number(copyParam.flowlineMxKey)
    );

    if (!flowline) {
      return;
    }

    if (!flowline.lineTexts) {
      flowline.lineTexts = [];
    }

    flowline.lineTexts.push({
      key: copyParam.key,
      position: copyParam.position,
      text: copyParam.text
    });
  }

  private changeLineText(param, json) {
    const copyParam = _.cloneDeep(param);
    const flowline = _.find(
      json.flowlines || [],
      fl => Number(fl.mxKey) === Number(copyParam.flowlineMxKey)
    );

    if (!flowline || !flowline.lineTexts) {
      return;
    }

    const lineText = flowline.lineTexts.find(item => item.key === copyParam.key);

    lineText.position = copyParam.position;
    lineText.text = copyParam.text;
  }

  private deleteLineText(param, json) {
    const flowline = _.find(
      json.flowlines || [],
      fl => Number(fl.mxKey) === Number(param.flowlineMxKey)
    );

    if (!flowline) {
      return;
    }

    flowline.lineTexts = (flowline.lineTexts || []).filter(item => item.key !== param.key);
  }

  private createInteraction(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.interactions) {
      json.interactions = [];
    }

    const flowline = _.find(
      json.flowlines,
      fl => Number(fl.mxKey) === Number(copyParam.flowlineMxKey)
    );

    if (!flowline) {
      return;
    }

    const interactionData = {
      flowlineMxKey: copyParam.flowlineMxKey,
      offset: copyParam.offset,
      description: copyParam.description,
      style: copyParam.style
    };

    if (typeof copyParam.index !== 'undefined') {
      json.interactions.splice(copyParam.index, 0, interactionData);
    } else {
      json.interactions.push(interactionData);
    }
  }

  private changeInteraction(param, json) {
    const copyParam = _.cloneDeep(param);
    const interaction = _.find(
      json.interactions,
      ia => Number(ia.flowlineMxKey) === Number(copyParam.flowlineMxKey)
    );

    if (!interaction) {
      return;
    }

    if (copyParam.offset) {
      interaction.offset = copyParam.offset;
    }

    if (typeof copyParam.description !== 'undefined') {
      interaction.description = copyParam.description;
    }

    if (copyParam.style) {
      if (!interaction.style) {
        interaction.style = {};
      }

      interaction.style = {...interaction.style, ...copyParam.style};
    }

    if (typeof copyParam.index !== 'undefined') {
      const currIndex = _.findIndex(
        json.interactions,
        (ia: any) => Number(ia.flowlineMxKey) === Number(copyParam.flowlineMxKey)
      );

      json.interactions.splice(copyParam.index, 0, json.interactions.splice(currIndex, 1)[0]);
    }
  }

  private deleteInteraction(param, json) {
    const interaction = _.find(
      json.interactions,
      ia => Number(ia.flowlineMxKey) === Number(param.flowlineMxKey)
    );

    if (!interaction) {
      return;
    }

    _.remove(
      json.interactions,
      (ia: any) => Number(ia.flowlineMxKey) === Number(param.flowlineMxKey)
    );
  }

  /** connector **/
  private addConnector(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.flowlines) {
      json.flowlines = [];
    }

    const flowline = _.find(
      json.flowlines,
      fl => Number(fl.mxKey) === Number(copyParam.flowlineMxKey)
    );

    if (!flowline) {
      return;
    }

    if (copyParam.srcType === 'connector') {
      flowline.srcType = 'connector';
      flowline.srcId = copyParam.mxKey;
    } else if (copyParam.tgtType === 'connector') {
      flowline.tgtType = 'connector';
      flowline.tgtId = copyParam.mxKey;
    }

    if (!copyParam.customStyle) {
      copyParam.customStyle = false;
    }

    flowline.connector = {
      mxKey: copyParam.mxKey,
      position: copyParam.position,
      styleObject: copyParam.styleObject,
      customStyle: copyParam.customStyle
    };
  }

  private changeConnector(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.flowlines) {
      json.flowlines = [];
    }

    const flowline = _.find(json.flowlines, function (f) {
      return Number(f.mxKey) === Number(copyParam.flowlineMxKey);
    });

    if (!flowline) {
      return;
    }

    if (flowline.connector) {
      if (copyParam.position) {
        flowline.connector.position = copyParam.position;
      }

      if (copyParam.style) {
        flowline.connector.style = copyParam.style;
      }

      if (copyParam.styleObject) {
        flowline.connector.styleObject = copyParam.styleObject;
      }

      if (typeof copyParam.customStyle !== 'undefined') {
        flowline.connector.customStyle = copyParam.customStyle;
      }
    }
  }

  private deleteConnector(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.flowlines) {
      json.flowlines = [];
    }

    const flowline = _.find(
      json.flowlines,
      fl => Number(fl.mxKey) === Number(copyParam.flowlineMxKey)
    );

    if (!flowline) {
      return;
    }

    if (flowline.srcType === 'connector') {
      flowline.srcType = 'none';
      delete flowline.srcId;
    }

    if (flowline.tgtType === 'connector') {
      flowline.tgtType = 'none';
      delete flowline.tgtId;
    }

    const copyPasteConnector =
      LocalStorageService.get<CopyPasteConnectorData>(DE_CONNECTOR_LINK_CACHE);

    if (copyPasteConnector && copyPasteConnector.link.flowline?.id === String(flowline.mxKey)) {
      LocalStorageService.remove(DE_CONNECTOR_LINK_CACHE);
    }

    delete flowline.connector;
  }

  /** terminator **/

  private addTerminator(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.flowlines) {
      json.flowlines = [];
    }

    const flowline = _.find(
      json.flowlines,
      fl => Number(fl.mxKey) === Number(copyParam.flowlineMxKey)
    );

    if (!flowline) {
      return;
    }

    if (!copyParam.customStyle) {
      copyParam.customStyle = false;
    }

    if (copyParam.srcType === 'terminator') {
      flowline.srcType = 'terminator';
      flowline.srcId = copyParam.mxKey;
    } else if (copyParam.tgtType === 'terminator') {
      flowline.tgtType = 'terminator';
      flowline.tgtId = copyParam.mxKey;
    }

    flowline.terminator = {
      mxKey: copyParam.mxKey,
      position: copyParam.position,
      styleObject: copyParam.styleObject,
      customStyle: copyParam.customStyle
    };
  }

  private changeTerminator(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.flowlines) {
      json.flowlines = [];
    }

    const flowline = _.find(json.flowlines, function (f) {
      return Number(f.mxKey) === Number(copyParam.flowlineMxKey);
    });

    if (!flowline) {
      return;
    }

    if (flowline.terminator) {
      if (copyParam.position) {
        flowline.terminator.position = copyParam.position;
      }

      if (copyParam.style) {
        flowline.terminator.style = copyParam.style;
      }

      if (copyParam.styleObject) {
        flowline.terminator.styleObject = copyParam.styleObject;
      }

      if (typeof copyParam.customStyle !== 'undefined') {
        flowline.terminator.customStyle = copyParam.customStyle;
      }
    }
  }

  private deleteTerminator(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.flowlines) {
      json.flowlines = [];
    }

    const flowline = _.find(
      json.flowlines,
      fl => Number(fl.mxKey) === Number(copyParam.flowlineMxKey)
    );

    if (!flowline) {
      return;
    }

    if (flowline.srcType === 'terminator') {
      flowline.srcType = 'none';
      delete flowline.srcId;
    }

    if (flowline.tgtType === 'terminator') {
      flowline.tgtType = 'none';
      delete flowline.tgtId;
    }

    delete flowline.terminator;
  }

  /** flowline **/
  private createFlowline(param: IFlowlineJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.flowlines) {
      json.flowlines = [];
    }

    if (!copyParam.customStyle) {
      copyParam.customStyle = false;
    }

    const flowline = <any>{
      mxKey: copyParam.mxKey,
      srcType: copyParam.srcType,
      srcId: copyParam.srcId,
      tgtType: copyParam.tgtType,
      tgtId: copyParam.tgtId,
      text: copyParam.text,
      srcPosition: copyParam.srcPosition,
      tgtPosition: copyParam.tgtPosition,
      waypoints: copyParam.waypoints,
      connector: copyParam.connector,
      terminator: copyParam.terminator,
      customStyle: copyParam.customStyle,
      lineTexts: copyParam.lineTexts
    };

    if (copyParam.flForm) {
      flowline.flForm = copyParam.flForm;
    }

    if (copyParam.style) {
      flowline.style = copyParam.style;
    }

    if (copyParam.position) {
      flowline.position = copyParam.position;
    }

    let connectorMxKey = null;

    if (copyParam.srcType === 'connector') {
      connectorMxKey = copyParam.srcId;
    } else if (copyParam.tgtType === 'connector') {
      connectorMxKey = copyParam.tgtId;
    }

    if (flowline.connector) {
      flowline.connector.mxKey = connectorMxKey;
    }

    let terminatorMxKey = null;

    if (copyParam.srcType === 'terminator') {
      terminatorMxKey = copyParam.srcId;
    } else if (copyParam.tgtType === 'terminator') {
      terminatorMxKey = copyParam.tgtId;
    }

    if (flowline.terminator) {
      flowline.terminator.mxKey = terminatorMxKey;
    }

    json.flowlines.push(flowline);
  }

  private changeFlowline(param: IFlowlineJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.flowlines) {
      json.flowlines = [];
    }

    const flowline = _.find(json.flowlines, function (fl) {
      return Number(fl.mxKey) === Number(copyParam.mxKey);
    });

    if (!flowline) {
      return;
    }

    if (copyParam.srcType) {
      flowline.srcType = copyParam.srcType;

      if (copyParam.srcId) {
        flowline.srcId = copyParam.srcId;
      } else {
        delete flowline.srcId;
      }
    }

    if (copyParam.tgtType) {
      flowline.tgtType = copyParam.tgtType;

      if (copyParam.tgtId) {
        flowline.tgtId = copyParam.tgtId;
      } else {
        delete flowline.tgtId;
      }
    }

    flowline.text = _.isString(copyParam.text) ? copyParam.text : flowline.text;
    flowline.srcPosition = copyParam.srcPosition ? copyParam.srcPosition : flowline.srcPosition;
    flowline.tgtPosition = copyParam.tgtPosition ? copyParam.tgtPosition : flowline.tgtPosition;
    flowline.waypoints = copyParam.waypoints ? copyParam.waypoints : flowline.waypoints;

    if (copyParam.position) {
      flowline.position = copyParam.position;
    }

    if (typeof copyParam.customStyle !== 'undefined') {
      flowline.customStyle = copyParam.customStyle;
    }

    if (copyParam.style || copyParam.customStyle) {
      flowline.style = copyParam.style;
    }

    if (copyParam.flForm) {
      flowline.flForm = copyParam.flForm;
    }

    // remove connector if there is no source or target type
    if (
      copyParam.srcType &&
      copyParam.srcType !== 'connector' &&
      copyParam.tgtType &&
      copyParam.tgtType !== 'connector'
    ) {
      delete flowline.connector;
    }

    // remove terminator if there is no source or target type
    if (
      copyParam.srcType &&
      copyParam.srcType !== 'terminator' &&
      copyParam.tgtType &&
      copyParam.tgtType !== 'terminator'
    ) {
      delete flowline.terminator;
    }

    // if Connector
    if (copyParam.connector) {
      flowline.connector = copyParam.connector;
      // insert connector mx key
      let connectorMxKey = null;

      if (copyParam.srcType === 'connector') {
        connectorMxKey = copyParam.srcId;
      } else if (copyParam.tgtType === 'connector') {
        connectorMxKey = copyParam.tgtId;
      }

      flowline.connector.mxKey = connectorMxKey;
    }

    // if Terminator
    if (copyParam.terminator) {
      flowline.terminator = copyParam.terminator;
      // insert terminator mx key
      let terminatorMxKey = null;

      if (copyParam.srcType === 'terminator') {
        terminatorMxKey = copyParam.srcId;
      } else if (copyParam.tgtType === 'terminator') {
        terminatorMxKey = copyParam.tgtId;
      }

      flowline.terminator.mxKey = terminatorMxKey;
    }
  }

  private deleteFlowline(param: IFlowlineJson, json) {
    const copyParam = _.cloneDeep(param);

    _.remove(json.flowlines, (flow: any) => Number(flow.mxKey) === Number(copyParam.mxKey));
  }

  /** image **/

  private createImage(param: IImageJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.images) {
      json.images = [];
    }

    let image = null;

    if (copyParam.mxKey) {
      image = json.images.find(imageJson => Number(imageJson.mxKey) === Number(copyParam.mxKey));
    }

    if (image) {
      image.deleted = false;
      image.mxKey = copyParam.mxKey;
      image.position = copyParam.position;
      image.uuid = copyParam.uuid;
      image.locked = copyParam.locked;
      image.link = copyParam.link;
    } else {
      image = {
        mxKey: copyParam.mxKey,
        position: copyParam.position,
        uuid: copyParam.uuid,
        copyImage: copyParam.copyImage,
        locked: copyParam.locked,
        link: copyParam.link,
        deleted: false
      };
      json.images.push(image);
    }
  }

  private changeImage(param: IImageJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.images) {
      json.images = [];
    }

    const image = _.find(json.images, function (n) {
      return Number(n.mxKey) === Number(copyParam.mxKey);
    });

    if (image) {
      image.position = copyParam.position ? copyParam.position : image.position;

      if (typeof copyParam.link !== 'undefined') {
        image.link = copyParam.link;
      }

      if (typeof copyParam.locked !== 'undefined') {
        image.locked = copyParam.locked;
      }
    }
  }

  private deleteImage(param: IImageJson, json) {
    const copyParam = _.cloneDeep(param);

    const image = json.images.find(
      imageJson => Number(imageJson.mxKey) === Number(copyParam.mxKey)
    );

    if (image) {
      image.deleted = true;
    }
  }

  public updateImageUrl(param: IImageJson, json) {
    if (Array.isArray(json.images)) {
      const image = json.images.find(imageJson => Number(imageJson.mxKey) === Number(param.mxKey));

      if (image) {
        image.imageResized = param.url;
      }

      if (image && param.uuid) {
        image.uuid = param.uuid;
      }
    }
  }

  /** text **/

  private createText(param: ITextJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.texts) {
      json.texts = [];
    }

    if (!copyParam.customStyle) {
      copyParam.customStyle = false;
    }

    const text = <any>{
      mxKey: copyParam.mxKey,
      number: copyParam.number,
      text: copyParam.text,
      position: copyParam.position,
      customStyle: copyParam.customStyle,
      locked: copyParam.locked,
      link: copyParam.link
    };

    if (copyParam.style) {
      text.style = copyParam.style;
    }

    json.texts.push(text);
  }

  private changeText(param: ITextJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.texts) {
      json.texts = [];
    }

    const text = _.find(json.texts, t => {
      return Number(t.mxKey) === Number(copyParam.mxKey);
    });

    if (text) {
      text.number = copyParam.number ? copyParam.number : text.number;
      text.text = _.isString(copyParam.text) ? copyParam.text : text.text;
      text.position = copyParam.position ? copyParam.position : text.position;

      if (typeof copyParam.link !== 'undefined') {
        text.link = copyParam.link;
      }

      if (typeof copyParam.locked !== 'undefined') {
        text.locked = copyParam.locked;
      }
    }

    if (typeof copyParam.customStyle !== 'undefined') {
      text.customStyle = copyParam.customStyle;
    }

    if (copyParam.style || copyParam.customStyle) {
      text.style = copyParam.style;
    }
  }

  private deleteText(param, json) {
    // const copyParam = _.cloneDeep(param);

    _.remove(json.texts, (text: any) => Number(text.mxKey) === Number(param.mxKey));
  }

  /** note **/

  private changeNote(param: IStickerJson, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.notes) {
      json.notes = [];
    }

    const note = _.find(json.notes, n => {
      return Number(n.mxKey) === Number(copyParam.mxKey);
    });

    if (note) {
      note.text = _.isString(copyParam.text) ? copyParam.text : note.text;
      note.position = copyParam.position ? copyParam.position : note.position;
      note.paperClass = copyParam.paperClass ? copyParam.paperClass : note.paperClass;

      if (typeof copyParam.locked !== 'undefined') {
        note.locked = copyParam.locked;
      }

      if (copyParam.editor) {
        note.editor = copyParam.editor;
      } else {
        const {id, firstName, lastName} = this.authService.getUserDataSync();

        note.editor = {id, firstName, lastName};
      }

      note.updatedAt = copyParam.updatedAt ?? Date.now();
      note.tags = copyParam.tags || [];
    }
  }

  private createNote(param: IStickerJson, json) {
    const copyParam = _.cloneDeep(param);
    const {id, firstName, lastName} = this.authService.getUserDataSync();

    if (!json.notes) {
      json.notes = [];
    }

    const note = {
      mxKey: copyParam.mxKey,
      text: copyParam.text,
      position: copyParam.position,
      paperClass: copyParam.paperClass,
      locked: copyParam.locked,
      // author & editor data may come from firebase if it someones else sticky note
      author: copyParam.author ?? {id, firstName, lastName},
      editor: copyParam.editor ?? {id, firstName, lastName},
      createdAt: copyParam.createdAt ?? Date.now(),
      updatedAt: copyParam.updatedAt ?? Date.now()
    };

    json.notes.push(note);
  }

  private deleteNote(param, json) {
    _.remove(json.notes, (note: any) => Number(note.mxKey) === Number(param.mxKey));
  }

  /** resource **/

  private changeResource(param: IResourceJson, json) {
    const copyParam = _.cloneDeep(param);

    const activity = json.activities.find(a => Number(a.mxKey) === Number(copyParam.activityMxKey));

    if (!activity.resources) {
      activity.resources = [];
    }

    const resIndex = _.findIndex(
      activity.resources,
      (r: any) => Number(r.mxKey) === Number(copyParam.mxKey)
    );

    if (copyParam.rasci) {
      activity.resources[resIndex].rasci = {
        responsible: !!copyParam.rasci.responsible,
        accountable: !!copyParam.rasci.accountable,
        supporting: !!copyParam.rasci.supporting,
        consulted: !!copyParam.rasci.consulted,
        informed: !!copyParam.rasci.informed
      };
    }

    if (typeof copyParam.hidden !== 'undefined') {
      activity.resources[resIndex].hidden = !!copyParam.hidden;
    }

    if (typeof copyParam.main !== 'undefined') {
      activity.resources.forEach(resource => (resource.main = false));
      activity.resources[resIndex].main = !!copyParam.main;
    }

    if (copyParam.position) {
      const toIndex = copyParam.position - 1;
      const resources = activity.resources;

      resources.splice(toIndex, 0, resources.splice(resIndex, 1)[0]);
    }
  }

  private attachResource(param: IResourceJson, json) {
    const copyParam = _.cloneDeep(param);

    const resourceJson: any = {
      mxKey: copyParam.mxKey,
      name: copyParam.resourceName,
      type: copyParam.resourceType,
      resourceDbId: copyParam.resourceDbId,
      hidden: copyParam.hidden,
      position: copyParam.position,
      customStyle: copyParam.customStyle,
      icon: copyParam.icon || '',
      main: copyParam.main
    };

    if (copyParam.rasci) {
      resourceJson.rasci = {
        responsible: !!copyParam.rasci.responsible,
        accountable: !!copyParam.rasci.accountable,
        supporting: !!copyParam.rasci.supporting,
        consulted: !!copyParam.rasci.consulted,
        informed: !!copyParam.rasci.informed
      };
    } else {
      resourceJson.rasci = {
        responsible: false,
        accountable: false,
        supporting: false,
        consulted: false,
        informed: false
      };
    }

    const activity = json.activities.find(a => Number(a.mxKey) === Number(copyParam.activityMxKey));

    if (!activity.resources) {
      activity.resources = [];
    }

    if (copyParam.main) {
      activity.resources.forEach(resource => (resource.main = false));
    }

    if (copyParam.position) {
      const toIndex = Math.max(copyParam.position - 1, 0);

      activity.resources.splice(toIndex, 0, resourceJson);
    } else {
      activity.resources.push(resourceJson);
    }
  }

  private detachResource(param: IResourceJson, json) {
    const activity = json.activities.find(a => Number(a.mxKey) === Number(param.activityMxKey));

    _.remove(activity.resources, (r: any) => Number(r.mxKey) === Number(param.mxKey));
  }

  /** swimlane **/

  private changeSwimline(param, json) {
    const copyParam = _.cloneDeep(param);
    let currentLines;

    if (json.swimlines && json.swimlines.lines) {
      currentLines = json.swimlines.lines;
      const index = _.findIndex(currentLines, item => {
        return parseInt(item['mxKey'], 10) === parseInt(copyParam.mxKey, 10);
      });

      currentLines[index].text = _.isString(copyParam.text)
        ? copyParam.text
        : currentLines[index].text;
      currentLines[index].size = copyParam.size ? copyParam.size : currentLines[index].size;

      if (copyParam.style) {
        currentLines[index].style = copyParam.style ? copyParam.style : currentLines[index].style;
      }

      if (typeof copyParam.position !== 'undefined') {
        const toIndex = copyParam.position;

        currentLines.splice(toIndex, 0, currentLines.splice(index, 1)[0]);
      }

      json.swimlines.lines = currentLines;
    }
  }

  private changeSwimlinePool(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.swimlines) {
      json.swimlines = {};
    }

    json.swimlines.horizontal =
      typeof copyParam.horizontal !== 'undefined'
        ? copyParam.horizontal
        : json.swimlines.horizontal;

    if (copyParam.style && copyParam.style.borderColor) {
      json.swimlines.style.borderColor = copyParam.style.borderColor;
    }

    if (copyParam.style && copyParam.style.length) {
      json.swimlines.style.length = copyParam.style.length;
    }
  }

  private addSwimline(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.swimlines) {
      json.swimlines = {};
    }

    if (!json.swimlines.lines) {
      json.swimlines.lines = [];
    }

    json.swimlines.lines.push({
      mxKey: copyParam.mxKey,
      size: copyParam.size,
      style: copyParam.style,
      text: copyParam.text
    });
  }

  private createSwimlinePool(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.swimlines) {
      json.swimlines = {};
    }

    json.swimlines.horizontal = copyParam.horizontal;
    json.swimlines.position = copyParam.position;
    json.swimlines.style = copyParam.style;
  }

  private removeSwimline(param, json) {
    if (json.swimlines && json.swimlines.lines) {
      _.remove(json.swimlines.lines, item => {
        return parseInt(item['mxKey'], 10) === parseInt(param.mxKey, 10);
      });
    }
  }

  /** header **/
  private createLegendItem(param, json) {
    const copyParam = _.cloneDeep(param);

    if (!json.headerData) {
      json.headerData = {};
    }

    if (!json.headerData.legendItems) {
      json.headerData.legendItems = [];
    }

    json.headerData.legendItems.push(copyParam);
  }

  private deleteLegendItem(param, json) {
    if (json.headerData && json.headerData.legendItems) {
      _.remove(json.headerData.legendItems, item => {
        return parseInt(item['mxKey'], 10) === parseInt(param.mxKey, 10);
      });
    }
  }

  private editLegendItem(param, json) {
    if (json.headerData?.legendItems) {
      const itemIndex = _.findIndex(json.headerData?.legendItems, item => {
        return parseInt(item['mxKey'], 10) === parseInt(param.mxKey, 10);
      });

      if (itemIndex >= 0) {
        json.headerData.legendItems[itemIndex].text = param.text;
        json.headerData.legendItems[itemIndex].description = param.description;
      }
    }
  }

  private reorderLegendItem(param, json) {
    if (json.headerData && json.headerData.legendItems) {
      const itemIndex = _.findIndex(
        json.headerData.legendItems,
        (item: any) => Number(item.mxKey) === Number(param.mxKey)
      );

      json.headerData.legendItems.splice(
        param.position,
        0,
        json.headerData.legendItems.splice(itemIndex, 1)[0]
      );
    }
  }

  private changeHeaderLogo(param, json) {
    if (!json.headerData) {
      json.headerData = {};
    }

    json.headerData.logo = null;
  }

  private removeLogo(param, json) {
    json.headerData.logo = null;
  }

  private changeColor(param, json) {
    if (!json.headerData) {
      json.headerData = {};
    }

    if (!json.headerData.legendItems) {
      json.headerData.legendItems = [];
    }

    json.headerData.color = param.color;
  }

  /** diagram images **/
  private updateActivityImage(param, json) {
    const copyParam = _.cloneDeep(param);
    const activity = _.find(json.activities, act => Number(act.mxKey) === Number(copyParam.mxKey));

    if (
      activity &&
      (!activity.hasOwnProperty('imageUrl') || activity.imageUrl !== param.imageUrl)
    ) {
      activity.imageUrl = param.imageUrl;
      activity.image = param.image;
    }
  }
}
